import type {
  ChatMessage,
  ConversationalFormOption,
  MenuContent,
} from '@karehero/models';

import { message } from './chatMessages';

/** The reply that what Keni put back to confirm is right. */
export const CONFIRM_YES_OPTION_ID = 'confirm-yes';

/**
 * A care profile question the server asked, by the menu message asking it;
 * or what it put back to confirm, drawn as a menu of the replies to it.
 */
export interface ServerQuestion {
  messageId: string;
  content: MenuContent & { questionId: string };
  /** Puts back what's known to confirm: picking replies to it, not answers a question. */
  isConfirm?: boolean;
}

const asQuestion = (m: ChatMessage): ServerQuestion | undefined => {
  if (m.from !== 'system') return undefined;
  if (m.content.kind === 'menu' && m.content.questionId) {
    return {
      messageId: m.id,
      content: { ...m.content, questionId: m.content.questionId },
    };
  }
  if (m.content.kind === 'confirm') {
    return {
      messageId: m.id,
      content: {
        kind: 'menu',
        text: m.content.text,
        options: m.content.options,
        questionId: '',
      },
      isConfirm: true,
    };
  }
  return undefined;
};

/** The question still being asked: the last the server asked, with nothing said to it since. */
export const openQuestion = (
  messages: readonly ChatMessage[],
): ServerQuestion | undefined => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.from === 'user') return undefined;
    const question = asQuestion(m);
    if (question) return question;
  }
  return undefined;
};

/** The first answer to a question after it was asked: the message, and the options picked. */
const answerAfter = (
  messages: readonly ChatMessage[],
  index: number,
  question: ServerQuestion,
): { messageId: string; optionIds: string[] } | undefined => {
  if (question.isConfirm) return replyTo(messages, question.messageId);
  const { questionId } = question.content;
  for (const m of messages.slice(index + 1)) {
    // Asked again before it was answered: the answer is the later asking's.
    const asked = asQuestion(m);
    if (!asked?.isConfirm && asked?.content.questionId === questionId) {
      return undefined;
    }
    if (m.content.kind !== 'answers' || m.status === 'error') continue;
    const answer = m.content.answers.find((a) => a.questionId === questionId);
    if (answer) return { messageId: m.id, optionIds: answer.optionIds ?? [] };
  }
  return undefined;
};

/** The member's reply to what Keni put back to confirm: the message, and the reply picked. */
const replyTo = (
  messages: readonly ChatMessage[],
  confirmId: string,
): { messageId: string; optionIds: string[] } | undefined => {
  const reply = messages.find(
    (m) =>
      m.content.kind === 'confirmation' && m.content.confirms === confirmId,
  );
  if (reply?.content.kind !== 'confirmation') return undefined;
  return {
    messageId: reply.id,
    optionIds: [
      reply.content.isConfirmed
        ? CONFIRM_YES_OPTION_ID
        : CONFIRM_CHANGED_OPTION_ID,
    ],
  };
};

/** The reply that something in what Keni put back has changed. */
const CONFIRM_CHANGED_OPTION_ID = 'confirm-changed';

/** Answers read from what the member said, which their own message already shows. */
const isReadFromSaid = (m: ChatMessage) =>
  m.content.kind === 'answers' && !!m.content.readFrom;

/** Kept with the conversation for Keni, not shown in it. */
const isKeptOnly = (m: ChatMessage) => m.content.kind === 'skip';

/**
 * The messages as drawn: a question answered by picking shows the pick, in
 * place of the answer's own message, and one answered some other way folds
 * its options away. Several picks are made in rows of their own, so a
 * multi-select question always folds, and its answer shows as said. Answers
 * read from what the member said show only as what they said, and what's kept
 * only for Keni, such as a question it set aside, isn't shown.
 */
export const shownQuestions = (
  messages: readonly ChatMessage[],
): ChatMessage[] => {
  const openId = openQuestion(messages)?.messageId;
  const hiddenIds = new Set<string>();
  const shown = messages.map((m, i) => {
    const question = asQuestion(m);
    if (!question) return m;
    const { content } = question;
    const answer = answerAfter(messages, i, question);
    const pickedId = content.isMultiSelect ? undefined : answer?.optionIds[0];
    if (pickedId && answer) hiddenIds.add(answer.messageId);
    return {
      ...m,
      content: {
        ...content,
        pickedId,
        isClosed: !pickedId && (m.id !== openId || !!content.isMultiSelect),
      },
    };
  });
  return shown.filter(
    (m) => !hiddenIds.has(m.id) && !isReadFromSaid(m) && !isKeptOnly(m),
  );
};

/**
 * Answers a question with the options picked, as a message of the member's;
 * or replies to what Keni put back to confirm.
 */
export const answersMessage = (
  question: ServerQuestion,
  options: ConversationalFormOption[],
): ChatMessage =>
  question.isConfirm
    ? message('user', {
        kind: 'confirmation',
        text: options.map((o) => o.label).join(', '),
        confirms: question.messageId,
        isConfirmed: options.some((o) => o.id === CONFIRM_YES_OPTION_ID),
      })
    : message('user', {
        kind: 'answers',
        text: options.map((o) => o.label).join(', '),
        answers: [
          {
            questionId: question.content.questionId,
            optionIds: options.map((o) => o.id),
          },
        ],
      });
